import React from 'react';
import WriteFilesPanel from './WriteFilesPanel';
import { Button, Typography, Box, MenuItem, TextField, Chip, Checkbox, InputAdornment, IconButton, Tooltip } from '@mui/material';
import Loader from '../../loader.js';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import Menu from '@mui/material/Menu';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MicIcon from '@mui/icons-material/Mic';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Edit } from '@mui/icons-material';
import { canReviewOcr } from '../../utils/attachmentOcr';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
export default function ConsultationFilesPanel(props) {
  if (props.onApplySelection) return <WriteFilesPanel {...props} />;
  const { readOnly = false, onOcr, anchorEl, consultationLoading, convertToGMT8Date, deleteSelectedHistoryItems, editingConsultationId, fileInputRef, getFilteredHistoryItems, handleDeleteItem, handleDownloadReport, handleMenuClose, handlePatientFileUpload, handleStartEditTitle, handleTranscribeAudio, isSupportedTranscriptionFile, leftPanelWidth, recordingSelectedItem, searchQuery, selectedItem, selectedItems, setAlert, setOpenDialog, setPreviewItem, setPreviewOpen, setRecordingSelectedItem, setSearchQuery, setShowPastDocs, showPastDocs, sortConfig, toggleItemSelection, workflowMode } = props;
  const isLiveWorkflow = workflowMode === 'live-consultation';
  const fileListTitle = isLiveWorkflow ? 'Audio files' : 'All Files';
  const visibleHistoryItems = getFilteredHistoryItems().filter(item => {
    if (workflowMode === 'transcribe-recording' || isLiveWorkflow) {
      return item.type === 'Attachment' && isSupportedTranscriptionFile(item.title);
    }
    return !item.isSubItem || item.type === 'Document';
  });
  return (<Box sx={{ width: showPastDocs ? leftPanelWidth : 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E9EE', borderRadius: '14px', overflow: 'hidden' }}>
                  {showPastDocs ? (
                    <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 650, color: '#253346' }}>{fileListTitle}</Typography>
                      <Tooltip title={`Hide ${fileListTitle}`}><IconButton size="small" aria-label={`Hide ${fileListTitle}`} onClick={() => setShowPastDocs(false)}><KeyboardArrowLeftIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title={`Show ${fileListTitle}`}><IconButton aria-label={`Show ${fileListTitle}`} onClick={() => setShowPastDocs(true)}><KeyboardArrowRightIcon /></IconButton></Tooltip>
                  )}
                  {showPastDocs && (
                    <Box sx={{ px: 1.5, pb: 1.5, maxHeight: 600, overflowY: 'auto' }}>
                      {/* Upload belongs to the Write workflow; Live and Recordings have their own upload step. */}
                      {workflowMode === 'write-document' && <><Button
                        variant="contained" disableElevation
                        size="small"
                        fullWidth
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ mb: 1.5, backgroundColor: '#218C89', borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 600, '&:hover': { backgroundColor: '#197572' } }}
                        startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                      >
                        Upload file
                      </Button>
                      <input disabled={readOnly} ref={fileInputRef} type="file" hidden onClick={event => { event.target.value = null; }} onChange={handlePatientFileUpload} /></>}
                      {/* Search */}
                      <TextField
                        placeholder="Find a file…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mb: 1, backgroundColor: '#F7F9FA', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 12, '& fieldset': { borderColor: 'transparent' } } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {/* History items */}
                      {consultationLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><Loader /></Box>
                      ) : visibleHistoryItems.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <DescriptionIcon sx={{ fontSize: 32, color: '#D1D5DB', mb: 1 }} />
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>No items found</Typography>
                        </Box>
                      ) : (
                        visibleHistoryItems
                          .sort((a, b) => {
                            const key = sortConfig.key;
                            if (a[key] < b[key]) return sortConfig.direction === 'asc' ? -1 : 1;
                            if (a[key] > b[key]) return sortConfig.direction === 'asc' ? 1 : -1;
                            return 0;
                          })
                          .map((item) => (
                            <Box
                              key={item.id}
                              onClick={() => {
                                if (editingConsultationId !== null) {
                                  setAlert('Please save or cancel the current title edit before viewing another item.', 'warning');
                                  return;
                                }
                                if (workflowMode === 'transcribe-recording' || (workflowMode === 'live-consultation' && isSupportedTranscriptionFile(item.title))) {
                                  setRecordingSelectedItem(item);
                                  return;
                                }
                                setPreviewItem(item);
                                setPreviewOpen(true);
                              }}
                              draggable={workflowMode === 'transcribe-recording' || workflowMode === 'live-consultation'}
                              onDragStart={workflowMode === 'transcribe-recording' || workflowMode === 'live-consultation' ? (e) => {
                                e.dataTransfer.setData('itemId', item.id);
                                e.dataTransfer.effectAllowed = 'copy';
                              } : undefined}
                              sx={{ p: 1.25, borderRadius: '9px', border: '1px solid', borderColor: recordingSelectedItem?.id === item.id ? '#B6DAD7' : 'transparent', backgroundColor: recordingSelectedItem?.id === item.id ? '#F0F8F7' : 'transparent', mb: 0.5, cursor: workflowMode === 'transcribe-recording' ? 'grab' : 'pointer', '&:hover': { backgroundColor: '#F7F9FA' } }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#263747', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || 'Untitled'}</Typography>
                              <Typography variant="caption" sx={{ color: '#82909E', fontSize: 10.5 }}>{(item.title?.split('.').pop() || 'Audio').toUpperCase()} {'\u2022'} {item.type === 'Consultation' && item.sessionDate ? item.sessionDate : convertToGMT8Date(item.createdAt)}</Typography>
                              {onOcr && !readOnly && canReviewOcr(item) && <Button size="small" variant="outlined"
                                sx={{ mt: 0.5, textTransform: 'none' }}
                                onClick={event => { event.stopPropagation(); onOcr(item); }}>OCR / View text</Button>}
                              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Chip label={item.type === 'Consultation' ? 'transcript' : item.type === 'Document' ? 'document' : 'attachment'} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: item.type === 'Consultation' ? '#E5E7EB' : item.type === 'Document' ? '#DCFCE7' : '#FEF3C7', color: item.type === 'Consultation' ? '#6B7280' : item.type === 'Document' ? '#16A34A' : '#D97706' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isLiveWorkflow && <Tooltip title="Open file"><IconButton size="small" aria-label={`Open ${item.title}`} onClick={event => { event.stopPropagation(); setPreviewItem(item); setPreviewOpen(true); }} sx={{ color: '#71868B' }}><VisibilityOutlined sx={{ fontSize: 17 }} /></IconButton></Tooltip>}
                                  {(workflowMode === 'write-document') && (
                                    <Checkbox size="small" checked={item.isSelected || false} onChange={(e) => { e.stopPropagation(); toggleItemSelection(item.id); }} onClick={(e) => e.stopPropagation()} sx={{ p: 0, '&.Mui-checked': { color: '#29C0BB' } }} />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          ))
                      )}
                    </Box>
                  )}
                </Box>

                {/* Sidebar action buttons */}
                {showPastDocs && !isLiveWorkflow && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedItems.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => {
                        setOpenDialog({
                          open: true,
                          content: `Are you sure you want to delete ${selectedItems.length} selected item(s)? This action cannot be undone.`,
                          confirmAction: deleteSelectedHistoryItems
                        });
                      }}
                      sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' }, color: '#FFFFFF', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                      startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                    >
                      Delete Selected ({selectedItems.length})
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => fileInputRef.current.click()}
                    sx={{ borderColor: '#29C0BB', color: '#29C0BB', borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#1ca8a4', backgroundColor: '#F0FDFA' } }}
                    startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                  >
                    Upload File
                  </Button>
                  <input disabled={readOnly}
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onClick={(e) => { e.target.value = null; }}
                    onChange={handlePatientFileUpload}
                  />
                </Box>}

                {/* Selection info for write-document mode */}
                {showPastDocs && workflowMode === 'write-document' && selectedItems.length > 0 && (
                  <Box sx={{ backgroundColor: '#F0FDFA', border: '1px solid #A7F3D0', borderRadius: '12px', p: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#065F46' }}>
                      {selectedItems.length} files selected
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#059669', display: 'block' }}>
                      Click files above to select them for document generation
                    </Typography>
                  </Box>
                )}

                {/* Action Menu for sidebar items */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {isSupportedTranscriptionFile(selectedItem?.title) && (
                    <MenuItem onClick={() => handleTranscribeAudio(selectedItem)}>
                      <MicIcon fontSize="small" sx={{ mr: 1 }} />
                      Transcribe
                    </MenuItem>
                  )}
                  {selectedItem?.type === 'Document' && (
                    <MenuItem onClick={() => handleDownloadReport(selectedItem)}>
                      <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
                      Download
                    </MenuItem>
                  )}
                  {selectedItem?.type === 'Consultation' && (
                    <MenuItem onClick={() => handleStartEditTitle(selectedItem)}>
                      <Edit fontSize="small" sx={{ mr: 1 }} />
                      Edit Title
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => handleDeleteItem(selectedItem)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete
                  </MenuItem>
                </Menu>
              </Box>);
}
